import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner, Checkbox, Select, Skeleton } from '@meshble/molo';
import { api, must } from '../lib/api';
import { t } from '../lib/lingua';

/**
 * I campi con cui si sceglie una macchina da creare presso il provider:
 * quale progetto (la credenziale), dove (la regione) e quanto grande (la
 * taglia). Le regioni e le taglie le dice il provider, con la credenziale
 * scelta: un codice scritto a mano sbagliato si scoprirebbe dieci minuti
 * dopo dentro un job.
 *
 * Li usano il wizard del server e la dichiarazione «il server è perso», che
 * può creare da sé la destinazione.
 *
 * Del pannello OVH restano fuori, apposta: la chiave SSH (la mette Molo, e
 * chiude l'accesso iniziale al passo 23), il tipo di rete (l'IP è pubblico,
 * il DR vive di DNS) e l'immagine (Ubuntu 24.04, come vuole l'installazione).
 */
export interface MacchinaCloud {
  credentialId: string;
  region: string;
  size: string;
  /** Canone mensile invece della tariffa oraria: su OVH non si torna indietro fino a fine mese. */
  monthlyBilling: boolean;
  /** Istantanee del disco fatte da OVH ogni notte, a pagamento: in più ai backup di Molo. */
  providerBackups: boolean;
  /** Un Additional IP libero del progetto con cui nascere; vuoto: l'IP della macchina. */
  failoverIp: string;
  /** Compra un IP fisso nuovo, in alternativa a `failoverIp`. È la scelta predefinita. */
  buyFailoverIp: boolean;
}

export const MACCHINA_VUOTA: MacchinaCloud = {
  credentialId: '', region: '', size: '', monthlyBilling: false, providerBackups: false, failoverIp: '', buyFailoverIp: true,
};

/** Il valore della scelta «compra» nel campo IP fisso: non è un indirizzo. */
const COMPRA_IP = 'compra';

export function useIPs(slug: string) {
  return useQuery({
    queryKey: ['ips', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/ips', { params: { path: { slug } } })),
    staleTime: 30_000,
  });
}

export function useCredenzialiCloud(slug: string) {
  return useQuery({
    queryKey: ['cloud-credentials', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/cloud-credentials', { params: { path: { slug } } })),
  });
}

export function useCatalogo(slug: string, credentialId: string) {
  return useQuery({
    queryKey: ['cloud-catalog', slug, credentialId],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/cloud-credentials/{id}/catalog', { params: { path: { slug, id: credentialId } } })),
    enabled: credentialId !== '',
    staleTime: 5 * 60_000,
  });
}

/** «d2-4 — 2 vCPU, 4 GB, 50 GB», come si legge sulla console. */
function nomeTaglia(f: { name: string; vcpus: number; ramGb: number; diskGb: number }): string {
  return t('{nome} — {vcpu} vCPU, {ram} GB, {disco} GB di disco', { nome: f.name, vcpu: f.vcpus, ram: f.ramGb, disco: f.diskGb });
}

function Nota({ children }: { children: ReactNode }) {
  return (
    <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>{children}</p>
  );
}

export function ScegliMacchina({
  slug, value, onChange, senzaIP,
}: { slug: string; value: MacchinaCloud; onChange: (v: MacchinaCloud) => void; senzaIP?: boolean }) {
  const cred = useCredenzialiCloud(slug);
  const ips = useIPs(slug);
  // Solo gli IP che nessun server dell'hub ha: uno assegnato si sposta da
  // «Indirizzi IP», dove si vede a chi lo si toglie.
  const liberi = (ips.data?.ips ?? []).filter((ip) => !ip.serverId && !ip.pending);
  const credenziali = (cred.data?.credentials ?? []).filter((c) => c.provider === 'ovh');
  const credentialId = value.credentialId || credenziali[0]?.id || '';
  const catalogo = useCatalogo(slug, credentialId);
  const regioni = catalogo.data?.regions ?? [];
  const regione = regioni.find((r) => r.name === value.region) ?? regioni[0];
  const taglie = regione?.flavors ?? [];
  const taglia = taglie.find((f) => f.name === value.size)?.name ?? taglie.find((f) => f.name === 'd2-4')?.name ?? taglie[0]?.name ?? '';

  // Le scelte implicite — prima credenziale, prima regione, d2-4 — risalgono
  // al genitore appena si conoscono, così il bottone «Crea» sa cosa manda.
  if (credentialId !== value.credentialId || (regione && regione.name !== value.region) || (taglia && taglia !== value.size)) {
    queueMicrotask(() => onChange({ ...value, credentialId, region: regione?.name ?? value.region, size: taglia || value.size }));
  }

  if (cred.isSuccess && credenziali.length === 0) {
    return (
      <Banner tone="error" title={t('Nessun progetto OVH collegato')}>
        {t('Le chiavi API del progetto si collegano in Impostazioni: senza, un server si può solo collegare a mano.')}
      </Banner>
    );
  }
  return (
    <>
      <Select
        label={t('Progetto')}
        value={credentialId}
        options={credenziali.map((c) => ({ value: c.id, label: c.label }))}
        onChange={(e) => onChange({ ...value, credentialId: e.target.value, region: '', size: '' })}
      />
      {catalogo.isPending && credentialId && <Skeleton height="3rem" />}
      {catalogo.error && <Banner tone="error" title={t('OVH non risponde')}>{catalogo.error.message}</Banner>}
      {catalogo.isSuccess && regioni.length === 0 && (
        <Banner tone="error" title={t('Nessuna regione disponibile')}>{t('Il progetto non ha regioni attive con le istanze: controlla su OVH.')}</Banner>
      )}
      {regioni.length > 0 && (
        <>
          <Select
            label={t('Regione')}
            value={regione?.name ?? ''}
            options={regioni.map((r) => ({ value: r.name, label: r.name }))}
            onChange={(e) => onChange({ ...value, credentialId, region: e.target.value, size: '' })}
            help={t('Dove sta la macchina. Per un ripristino, una regione diversa da quella persa.')}
          />
          <Select
            label={t('Taglia')}
            value={taglia}
            options={taglie.map((f) => ({ value: f.name, label: nomeTaglia(f) }))}
            onChange={(e) => onChange({ ...value, credentialId, region: regione?.name ?? '', size: e.target.value })}
            help={t('Ubuntu 24.04, come vuole l’installazione.')}
          />
          {!senzaIP && (
            <Select
              label={t('IP fisso')}
              value={value.buyFailoverIp ? COMPRA_IP : value.failoverIp}
              options={[
                { value: COMPRA_IP, label: t('Comprane uno nuovo — 1,99 €/mese') },
                ...liberi.map((ip) => ({ value: ip.ip, label: `${ip.ip}${ip.country ? ` (${ip.country})` : ''}${ip.attachedTo ? ` — ${t('su OVH ancora a {macchina}', { macchina: ip.attachedTo })}` : ''}` })),
                { value: '', label: t('Nessuno: quello della macchina') },
              ]}
              onChange={(e) => onChange({
                ...value,
                buyFailoverIp: e.target.value === COMPRA_IP,
                failoverIp: e.target.value === COMPRA_IP ? '' : e.target.value,
              })}
              help={value.buyFailoverIp
                ? t('Molo lo compra su OVH appena la macchina esiste, geolocalizzato in Italia. Segue il servizio: in una DR passa al server nuovo senza toccare il DNS, e se il server viene dismesso resta nel progetto, libero.')
                : t('Un Additional IP del progetto: segue il servizio, e in una DR passa al server nuovo senza toccare il DNS. Dev’essere della stessa zona della regione.')}
            />
          )}
          <Checkbox
            checked={value.monthlyBilling}
            onCheckedChange={(v) => onChange({ ...value, monthlyBilling: v === true })}
            label={t('Canone mensile invece che a ore')}
          />
          <Nota>{t('Costa meno per una macchina che resta, ma OVH non lo revoca fino a fine mese. Per un’emergenza, lascia a ore.')}</Nota>
          <Checkbox
            checked={value.providerBackups}
            onCheckedChange={(v) => onChange({ ...value, providerBackups: v === true })}
            label={t('Istantanee automatiche di OVH')}
          />
          <Nota>
            {t('Ogni notte OVH fotografa il disco e tiene sette copie, a pagamento. Sono in più ai backup di Molo, non al loro posto: «Siamo pronti?» guarda solo quelli di Molo.')}
          </Nota>
        </>
      )}
    </>
  );
}
